import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../hooks/useApi';
import SowSelect from '../shared/SowSelect';
import Modal from '../shared/Modal';
import Avatar from '../shared/Avatar';
import LoadBar from '../shared/LoadBar';
import { makeCalendar } from '../../../../src/lib/schedule.js';
import { personDaysOnTask } from '../../../../src/lib/capacity.js';

// Days of work, the unit the rest of resourcing uses. Stored as planned hours
// (7.5 a day); the share the scheduler needs is worked out from the person's
// own working days in the task's dates.
const DAY_HOURS = 7.5;
const PLANNER_CAL = makeCalendar();
const r2 = (n) => Math.round(n * 100) / 100;

// ── Date helpers ───────────────────────────────────────────────────────────────

function utcDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function addWorkdays(startStr, days) {
  if (!startStr) return startStr;
  const target = Math.min(Math.max(Math.floor(days), 0), 1300); // 0 days → return start unchanged
  const d = utcDate(startStr);
  if (isNaN(d.getTime())) return startStr;
  let added = 0;
  while (added < target) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

function weekMonday(date) {
  const d = utcDate(date);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = utcDate(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function addMonths(dateStr, n) {
  const [y, m] = dateStr.split('-').map(Number);
  let nm = m - 1 + n;
  let ny = y + Math.floor(nm / 12);
  nm = ((nm % 12) + 12) % 12;
  return `${ny}-${String(nm + 1).padStart(2, '0')}-01`;
}

function firstOfMonth(dateStr) {
  const [y, m] = dateStr.split('-').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

function calDaysBetween(start, end) {
  return Math.round((utcDate(end) - utcDate(start)) / 86400000);
}

function weekdaysOfMonth(monthStart) {
  const [y, m] = monthStart.split('-').map(Number);
  const days = [];
  let cur = monthStart;
  while (true) {
    const [cy, cm] = cur.split('-').map(Number);
    if (cy !== y || cm !== m) break;
    const dow = utcDate(cur).getUTCDay();
    if (dow !== 0 && dow !== 6) days.push(cur);
    cur = addDays(cur, 1);
  }
  return days;
}

function fmtDay(dateStr) {
  return utcDate(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

function fmtDayHeader(dateStr) {
  return utcDate(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function fmtWeek(mondayStr) {
  const mon = utcDate(mondayStr);
  const fri = new Date(Date.UTC(mon.getUTCFullYear(), mon.getUTCMonth(), mon.getUTCDate() + 4));
  return `${mon.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${fri.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`;
}

function fmtMonthYear(dateStr) {
  return utcDate(dateStr).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function fmtWeekRange(days) {
  if (!days.length) return '';
  const fmt = d => utcDate(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${fmt(days[0])} – ${fmt(days[days.length - 1])}`;
}

function leaveLabel(type) {
  const map = {
    annual: 'Annual Leave', sick: 'Sick Leave', wellness: 'Wellness Day',
    carry_over: 'Carried Over', tenure_annual: 'Tenure Leave',
    benefits_annual: 'Benefits Leave', unpaid: 'Unpaid Leave', other: 'Leave',
  };
  return map[type] || 'On Leave';
}

// ── Colour palette ─────────────────────────────────────────────────────────────

const PALETTE = [
  { bg: 'bg-blue-100',    border: 'border-blue-300',    text: 'text-blue-800',    hex: '#3B82F6' },
  { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-800', hex: '#10B981' },
  { bg: 'bg-violet-100',  border: 'border-violet-300',  text: 'text-violet-800',  hex: '#8B5CF6' },
  { bg: 'bg-rose-100',    border: 'border-rose-300',    text: 'text-rose-800',    hex: '#F43F5E' },
  { bg: 'bg-cyan-100',    border: 'border-cyan-300',    text: 'text-cyan-800',    hex: '#06B6D4' },
  { bg: 'bg-orange-100',  border: 'border-orange-300',  text: 'text-orange-800',  hex: '#F59E0B' },
  { bg: 'bg-teal-100',    border: 'border-teal-300',    text: 'text-teal-800',    hex: '#14B8A6' },
  { bg: 'bg-pink-100',    border: 'border-pink-300',    text: 'text-pink-800',    hex: '#EC4899' },
  { bg: 'bg-indigo-100',  border: 'border-indigo-300',  text: 'text-indigo-800',  hex: '#6366F1' },
  { bg: 'bg-lime-100',    border: 'border-lime-300',    text: 'text-lime-800',    hex: '#84CC16' },
];

const SLOT_COLORS = PALETTE.map(p => p.hex);

function useSowColorMap(tasks) {
  return useMemo(() => {
    const sowIds = [...new Set(tasks.map(t => t.sow_id).filter(Boolean))];
    const map = {};
    sowIds.forEach((id, i) => { map[id] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [tasks]);
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function WeekPlannerPage() {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // ── Shared state ──
  const [view,           setView]           = useState('week');
  const [teams,          setTeams]          = useState([]);
  const [people,         setPeople]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  // ── Week-view state ──
  const [leaveMap,     setLeaveMap]     = useState({});
  const [leaveDetails, setLeaveDetails] = useState({});
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [weekStart,    setWeekStart]    = useState(() => weekMonday(todayStr));

  // ── Month-view state ──
  const [monthStart,    setMonthStart]    = useState(() => firstOfMonth(todayStr));
  const [plannerSlots,  setPlannerSlots]  = useState([]);
  const [plannerTasks,  setPlannerTasks]  = useState([]); // tasks for team tray
  const [sows,          setSows]          = useState([]); // active SOWs for dropdown
  const [slotsLoading,  setSlotsLoading]  = useState(false);
  const [tasksLoading,  setTasksLoading]  = useState(false);

  // ── Drag state ──
  const [movingSlot,        setMovingSlot]        = useState(null);
  const [resizingSlot,      setResizingSlot]      = useState(null);
  const [draggingTask,      setDraggingTask]      = useState(null);  // from unallocated tray
  const [draggingOwnedTask, setDraggingOwnedTask] = useState(null);  // already-assigned task in grid
  const [dropTarget,        setDropTarget]        = useState(null);

  const movingSlotRef        = useRef(null);
  const resizingSlotRef      = useRef(null);
  const draggingTaskRef      = useRef(null);
  const draggingOwnedTaskRef = useRef(null);

  // ── Modal state ──
  const EMPTY_FORM = {
    sow_id: null, task_id: null, person_id: null, date: '',
    duration_days: '',              // editable on existing task
    work_days: '',                  // days of work for the person on an existing task
    depends_on_task_id: null,       // dependency task (existing task)
    is_new_task: false,             // toggle to create a brand-new SOW task
    new_task_name: '',              // name for the new task
    new_task_duration: '1',         // duration for the new task
    new_task_work_days: '1',        // days of work for the new task
    new_task_team_id: null,         // team for the new task
    new_task_depends_on: null,      // dependency for new task
  };
  const [modal,          setModal]          = useState(null);
  const [modalForm,      setModalForm]      = useState(EMPTY_FORM);
  const [modalSaving,    setModalSaving]    = useState(false);
  const [slotError,      setSlotError]      = useState(null);
  const [modalSowTasks,  setModalSowTasks]  = useState([]);

  // ── Initial load: teams, people, week-view tasks, SOWs ──
  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const timeout = setTimeout(() => { setLoading(false); setLoadError(true); }, 20000);
    // Tasks come from planner-tasks below. Resource Management's own feed
    // (forecast/schedule) used to be fetched here too and never read; it is
    // guarded by the Resource Management page, which Planner users such as
    // engineers do not hold, so asking for it would fail the whole load.
    Promise.all([
      api.get('teams'),
      api.get('people', { status: 'active' }),
      api.get('sows', { status: 'Active', limit: 500 }),
    ]).then(([t, p, sw]) => {
      clearTimeout(timeout);
      const activeTeams = t.filter(x => x.is_active !== 0);
      setTeams(activeTeams);
      setPeople(p);
      setSows(sw);
      if (activeTeams.length > 0) setSelectedTeamId(prev => prev || activeTeams[0].id);
      setLoading(false);
    }).catch(() => { clearTimeout(timeout); setLoading(false); setLoadError(true); });
  }, []);

  // ── Fetch tasks for the SOW selected in the modal ──
  useEffect(() => {
    if (!modalForm.sow_id) { setModalSowTasks([]); return; }
    api.get(`sows/${modalForm.sow_id}/project-tasks`)
      .then(t => setModalSowTasks(t || []))
      .catch(() => setModalSowTasks([]));
  }, [modalForm.sow_id]);

  // ── Leave reload (week + month views) ──
  useEffect(() => {
    const from = view === 'week' ? weekStart : monthStart;
    const to   = view === 'week' ? addDays(weekStart, 4) : addDays(addMonths(monthStart, 1), -1);
    setLeaveLoading(true);
    api.get('leave-requests', { status: 'approved', from, to })
      .then(requests => {
        const map = {}, details = {};
        for (const req of requests) {
          const pid = req.person_id;
          if (!map[pid]) map[pid] = new Set();
          let cur = req.start_date;
          while (cur <= req.end_date) {
            const dow = new Date(cur + 'T00:00:00').getDay();
            if (dow !== 0 && dow !== 6) {
              // A half-day edge is still marked as leave, but says which half,
              // so a Wednesday-lunchtime start does not read as a whole day off.
              const half = req.start_half === 'pm' && cur === req.start_date ? ' (PM)'
                : req.end_half === 'am' && cur === req.end_date ? ' (AM)' : '';
              map[pid].add(cur);
              details[`${pid}|${cur}`] = leaveLabel(req.leave_type) + half;
            }
            cur = addDays(cur, 1);
          }
        }
        setLeaveMap(map);
        setLeaveDetails(details);
        setLeaveLoading(false);
      })
      .catch(() => { setLeaveMap({}); setLeaveDetails({}); setLeaveLoading(false); });
  }, [weekStart, monthStart, view]);

  // ── Month: reload planner slots ──
  useEffect(() => {
    if (view !== 'month' || !selectedTeamId) return;
    const from = monthStart;
    const to   = addDays(addMonths(monthStart, 1), -1);
    setSlotsLoading(true);
    api.get('planner-slots', { team_id: selectedTeamId, from, to })
      .then(slots => { setPlannerSlots(slots); setSlotsLoading(false); })
      .catch(() => { setPlannerSlots([]); setSlotsLoading(false); });
  }, [monthStart, selectedTeamId, view]);

  // ── Planner tasks — load ALL active tasks once, filter client-side by team/person ──
  // Not filtered by team_id on the server to avoid team_id mismatch edge cases.
  useEffect(() => {
    setTasksLoading(true);
    api.get('planner-tasks')
      .then(t => { setPlannerTasks(t || []); setTasksLoading(false); })
      .catch(() => { setPlannerTasks([]); setTasksLoading(false); });
  }, []);

  // ── The team's capacity and load this week, by day, from the shared engine ──
  // Re-read whenever the task list changes, so a drag or a save shows its
  // effect on everybody's week straight away.
  const [teamLoad, setTeamLoad] = useState(null);
  useEffect(() => {
    if (view !== 'week' || !selectedTeamId) return;
    let cancelled = false;
    api.get('resource-load', { team_id: selectedTeamId, from: weekStart, to: addDays(weekStart, 4), days: 1 })
      .then(d => { if (!cancelled) setTeamLoad(d); })
      .catch(() => { if (!cancelled) setTeamLoad(null); });
    return () => { cancelled = true; };
  }, [view, selectedTeamId, weekStart, plannerTasks]);
  const loadByPerson = useMemo(() => {
    const m = new Map();
    for (const row of teamLoad?.people || []) {
      m.set(row.person_id, { week: row.weeks?.[0] || null, days: new Map((row.days || []).map(d => [d.date, d])) });
    }
    return m;
  }, [teamLoad]);

  // ── Shared memos ──
  const teamPeople   = useMemo(() => people.filter(p => p.team_id === selectedTeamId), [people, selectedTeamId]);
  const selectedTeam = useMemo(() => teams.find(t => t.id === selectedTeamId), [teams, selectedTeamId]);

  // Team tasks relevant to the selected team (by task.team_id OR owner's team membership)
  const teamPeopleIds = useMemo(() => new Set(teamPeople.map(p => p.id)), [teamPeople]);
  const teamPlannerTasks = useMemo(() =>
    plannerTasks.filter(t =>
      t.team_id === selectedTeamId ||
      (t.owner_id && teamPeopleIds.has(t.owner_id))
    ),
  [plannerTasks, selectedTeamId, teamPeopleIds]);

  // ── Week-view memos ──
  // Week-view task map: team tasks with owner + start_date → rendered in person's column
  const tasksWithEnd = useMemo(() =>
    teamPlannerTasks
      .filter(t => t.owner_id && t.start_date)
      // The engine stores an inclusive finish, so the exclusive end this view
      // wants is the working day after it. Fall back only for a row the
      // scheduler has not reached yet.
      .map(t => ({ ...t, end_excl: t.end_date ? addWorkdays(t.end_date, 1) : addWorkdays(t.start_date, t.duration_days || 1) })),
  [teamPlannerTasks]);
  const sowColorMap = useSowColorMap(tasksWithEnd);
  const weekDays = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const taskDayMap = useMemo(() => {
    const map = {};
    for (const t of tasksWithEnd) {
      if (!t.owner_id || !t.start_date || !t.end_excl) continue;
      for (const day of weekDays) {
        const d = day;
        if (d >= t.start_date && d < t.end_excl) {
          const key = `${t.owner_id}|${day}`;
          if (!map[key]) map[key] = [];
          map[key].push(t);
        }
      }
    }
    return map;
  }, [tasksWithEnd, weekDays]);
  const weekStats = useMemo(() => {
    let taskDays = 0, leaveDays = 0;
    for (const p of teamPeople) {
      for (const day of weekDays) {
        if (leaveMap[p.id]?.has(day)) leaveDays++;
        else if ((taskDayMap[`${p.id}|${day}`] || []).length > 0) taskDays++;
      }
    }
    return { taskDays, leaveDays };
  }, [teamPeople, weekDays, taskDayMap, leaveMap]);

  // ── Month-view memos ──
  const monthDays  = useMemo(() => weekdaysOfMonth(monthStart), [monthStart]);
  const monthWeeks = useMemo(() => {
    const weeks = [];
    let week = [];
    for (const day of monthDays) {
      const dow = utcDate(day).getUTCDay();
      if (dow === 1 && week.length > 0) { weeks.push(week); week = []; }
      week.push(day);
    }
    if (week.length > 0) weeks.push(week);
    return weeks;
  }, [monthDays]);

  // slotDayMap: "personId|date" → [{ slot, isStart, isEnd }]
  const slotDayMap = useMemo(() => {
    const map = {};
    for (const slot of plannerSlots) {
      const endDate = slot.end_date || slot.date;
      let cur = slot.date;
      while (cur <= endDate) {
        const dow = utcDate(cur).getUTCDay();
        if (dow !== 0 && dow !== 6) {
          const key = `${slot.person_id}|${cur}`;
          if (!map[key]) map[key] = [];
          map[key].push({ slot, isStart: cur === slot.date, isEnd: cur === endDate });
        }
        cur = addDays(cur, 1);
      }
    }
    return map;
  }, [plannerSlots]);

  // taskOwnerDayMap: "personId|date" → [task]
  // Assigned SOW tasks (have owner_id + start_date) shown as indicators in month grid
  const monthEnd = useMemo(() => addDays(addMonths(monthStart, 1), -1), [monthStart]);
  const taskOwnerDayMap = useMemo(() => {
    const map = {};
    for (const t of teamPlannerTasks) {
      if (!t.owner_id || !t.start_date) continue;
      // Inclusive end: duration 1 → ends on start_date; duration N → ends N-1 workdays after start
      const dur = t.duration_days || 1;
      const taskEnd = t.end_date || addWorkdays(t.start_date, dur - 1);
      // Clamp to current month
      const rangeStart = t.start_date > monthStart ? t.start_date : monthStart;
      const rangeEnd   = taskEnd     < monthEnd    ? taskEnd     : monthEnd;
      if (rangeStart > rangeEnd) continue;
      let cur = rangeStart;
      while (cur <= rangeEnd) {
        const dow = utcDate(cur).getUTCDay();
        if (dow !== 0 && dow !== 6) {
          const key = `${t.owner_id}|${cur}`;
          if (!map[key]) map[key] = [];
          if (!map[key].find(x => x.id === t.id)) map[key].push(t);
        }
        cur = addDays(cur, 1);
      }
    }
    return map;
  }, [teamPlannerTasks, monthStart, monthEnd]);

  // sowHexMap: sow_id → hex colour for tray cards
  const sowHexMap = useMemo(() => {
    const sowIds = [...new Set(teamPlannerTasks.map(t => t.sow_id).filter(Boolean))];
    const map = {};
    sowIds.forEach((id, i) => { map[id] = SLOT_COLORS[i % SLOT_COLORS.length]; });
    return map;
  }, [plannerTasks]);

  // Unallocated tray: tasks with no owner, OR assigned but no start_date (unscheduled).
  // Tasks WITH owner AND start_date already appear in the grid via taskOwnerDayMap.
  const unallocatedTasks = useMemo(() => {
    if (!selectedTeamId) return [];
    return teamPlannerTasks.filter(t => !t.owner_id || !t.start_date);
  }, [teamPlannerTasks, selectedTeamId]);

  // ── Slot CRUD ──
  async function reloadSlots() {
    if (!selectedTeamId) return;
    const from = monthStart;
    const to   = addDays(addMonths(monthStart, 1), -1);
    const slots = await api.get('planner-slots', { team_id: selectedTeamId, from, to });
    setPlannerSlots(slots);
  }

  async function createSlot(data) {
    try {
      setSlotError(null);
      await api.post('planner-slots', { ...data, team_id: selectedTeamId });
      await reloadSlots();
    } catch (err) {
      console.error('createSlot failed:', err);
      setSlotError('Could not save activity. Make sure migration 0027_planner_slots.sql has been applied.');
    }
  }

  async function updateSlotData(id, data) {
    try {
      setSlotError(null);
      await api.put(`planner-slots/${id}`, data);
      await reloadSlots();
    } catch (err) {
      console.error('updateSlotData failed:', err);
      setSlotError('Could not update activity.');
    }
  }

  async function deleteSlot(id) {
    try {
      await api.delete(`planner-slots/${id}`);
      setPlannerSlots(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('deleteSlot failed:', err);
    }
  }

  // ── Drag handlers ──
  function onSlotDragStart(e, slot) {
    movingSlotRef.current   = { slot };
    resizingSlotRef.current = null;
    draggingTaskRef.current = null;
    setMovingSlot({ slot });
    setResizingSlot(null);
    setDraggingTask(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slot.id);
  }

  function onResizeDragStart(e, slot) {
    e.stopPropagation();
    resizingSlotRef.current = { slot };
    movingSlotRef.current   = null;
    draggingTaskRef.current = null;
    setResizingSlot({ slot });
    setMovingSlot(null);
    setDraggingTask(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slot.id);
  }

  function onTaskDragStart(e, task) {
    draggingTaskRef.current      = { task };
    movingSlotRef.current        = null;
    resizingSlotRef.current      = null;
    draggingOwnedTaskRef.current = null;
    setDraggingTask({ task });
    setMovingSlot(null);
    setResizingSlot(null);
    setDraggingOwnedTask(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'task:' + task.id);
  }

  // Drag an already-assigned task directly from the grid (month or week view)
  function onOwnedTaskDragStart(e, task) {
    draggingOwnedTaskRef.current = { task };
    draggingTaskRef.current      = null;
    movingSlotRef.current        = null;
    resizingSlotRef.current      = null;
    setDraggingOwnedTask({ task });
    setDraggingTask(null);
    setMovingSlot(null);
    setResizingSlot(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'owned:' + task.id);
  }

  function onCellDragOver(e, personId, date) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ personId, date });
  }

  async function onCellDrop(e, personId, date) {
    e.preventDefault();
    const dt  = draggingTaskRef.current;
    const ot  = draggingOwnedTaskRef.current;
    const ms  = movingSlotRef.current;
    const rs  = resizingSlotRef.current;

    draggingTaskRef.current      = null;
    draggingOwnedTaskRef.current = null;
    movingSlotRef.current        = null;
    resizingSlotRef.current      = null;
    setDraggingTask(null);
    setDraggingOwnedTask(null);
    setMovingSlot(null);
    setResizingSlot(null);
    setDropTarget(null);

    // Drop from unallocated task tray — update sow_project_task directly, no slot needed
    if (dt) {
      const { task } = dt;
      if (task.sow_id) {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, {
          owner_id:   personId,
          start_date: date,
        });
      }
      const [t] = await Promise.all([api.get('planner-tasks'), reloadSlots()]);
      setPlannerTasks(t || []);
      return;
    }

    // Drop from an already-assigned task in the grid — reassign owner + date
    if (ot) {
      const { task } = ot;
      if (task.sow_id) {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, {
          owner_id:   personId,
          start_date: date,
        });
      }
      const [t] = await Promise.all([api.get('planner-tasks'), reloadSlots()]);
      setPlannerTasks(t || []);
      return;
    }

    // Move existing planner slot (non-task slots: leave notes etc.)
    if (ms) {
      const { slot } = ms;
      const durDays  = slot.end_date ? calDaysBetween(slot.date, slot.end_date) : 0;
      const newEnd   = durDays > 0 ? addDays(date, durDays) : null;
      await updateSlotData(slot.id, {
        person_id: personId,
        date,
        end_date:  newEnd,
        slot_type: durDays > 0 ? 'full_day' : slot.slot_type,
      });
      // If the slot is linked to a task, keep the task in sync
      if (slot.task_id && slot.sow_id) {
        await api.put(`sows/${slot.sow_id}/project-tasks/${slot.task_id}`, {
          owner_id: personId, start_date: date,
        });
        api.get('planner-tasks').then(t => setPlannerTasks(t || []));
      }
      return;
    }

    // Resize existing slot
    if (rs) {
      const { slot } = rs;
      if (date >= slot.date) {
        await updateSlotData(slot.id, {
          end_date:  date === slot.date ? null : date,
          slot_type: date !== slot.date ? 'full_day' : slot.slot_type,
        });
      }
    }
  }

  function onDragEnd() {
    draggingTaskRef.current      = null;
    draggingOwnedTaskRef.current = null;
    movingSlotRef.current        = null;
    resizingSlotRef.current      = null;
    setMovingSlot(null);
    setResizingSlot(null);
    setDraggingTask(null);
    setDraggingOwnedTask(null);
    setDropTarget(null);
  }

  // ── Modal handlers ──
  // Open modal to assign a task to a person on a date (create mode)
  function openCreateModal(personId, date) {
    setModalSowTasks([]);
    setModalForm({ ...EMPTY_FORM, person_id: personId, date });
    setModal({ mode: 'create' });
  }

  // Open modal to edit an existing task assignment (edit mode)
  function openTaskModal(task) {
    setModalSowTasks([]);
    setModalForm({
      ...EMPTY_FORM,
      sow_id:             task.sow_id            || null,
      task_id:            task.id,
      person_id:          task.owner_id           || null,
      date:               task.start_date         || '',
      duration_days:      task.duration_days != null ? String(task.duration_days) : '',
      work_days:          String(daysOfWorkFor(task, task.owner_id)),
      depends_on_task_id: task.depends_on_task_id || null,
    });
    setModal({ mode: 'edit', task });
    // Always fetch tasks directly — useEffect only fires on sow_id change,
    // so repeated opens for the same SOW would leave the list empty.
    if (task.sow_id) {
      api.get(`sows/${task.sow_id}/project-tasks`)
        .then(t => setModalSowTasks(t || []))
        .catch(() => setModalSowTasks([]));
    }
  }

  // The working days a person has in a task's dates, and what their share
  // comes to in days. Without a person, the task's own working days.
  function availableDaysFor(task, personId, start, end) {
    const from = start || task?.start_date;
    const to = end || task?.end_date || from;
    if (!from) return 0;
    const person = personId ? people.find(p => p.id === personId) : null;
    if (person) return personDaysOnTask(person, from, to, 1, PLANNER_CAL);
    return Number(task?.duration_days) || 0;
  }
  function daysOfWorkFor(task, personId) {
    if (Number(task?.lead_planned_hours) > 0) return r2(Number(task.lead_planned_hours) / DAY_HOURS);
    const avail = availableDaysFor(task, personId);
    const share = (Number(task?.fte_pct) || 100) / 100;
    return r2((avail > 0 ? avail : (Number(task?.duration_days) || 0)) * share);
  }
  function shareFor(days, task, personId, start, end) {
    const avail = availableDaysFor(task, personId, start, end);
    return avail > 0 ? Math.min(100, Math.max(1, Math.round((days / avail) * 100))) : 100;
  }

  // Dates are the server's job. Every write to a project task runs the
  // scheduling engine (src/lib/schedule.js), which knows about FS/SS/FF/SF
  // links, lag, multiple predecessors, summary roll-up and the working
  // calendar. The copy of a cascade that used to live here understood none of
  // that and would quietly flatten a lagged link back to finish-to-start.
  async function refreshPlannerTasks() {
    const t = await api.get('planner-tasks').catch(() => []);
    setPlannerTasks(t || []);
  }

  async function onModalSave() {
    setModalSaving(true);
    try {
      const sowId = modalForm.sow_id;
      if (modalForm.is_new_task) {
        // ── Create brand-new SOW task then assign it ──
        if (!sowId || !modalForm.new_task_name.trim()) return;
        const durDays = parseFloat(modalForm.new_task_duration) || 1;
        const workDays = parseFloat(modalForm.new_task_work_days) || durDays;
        await api.post(`sows/${sowId}/project-tasks`, {
          name:               modalForm.new_task_name.trim(),
          duration_days:      durDays,
          // With a person: their days of work, and the share that comes to.
          // Without: the share of the task its team is asked for.
          fte_pct:            Math.min(modalForm.person_id ? 100 : 1000, Math.max(1, Math.round((workDays / durDays) * 100))),
          ...(modalForm.person_id ? { lead_planned_hours: r2(workDays * DAY_HOURS) } : {}),
          owner_id:           modalForm.person_id        || null,
          start_date:         modalForm.date             || null,
          team_id:            modalForm.new_task_team_id || null,
          depends_on_task_id: modalForm.new_task_depends_on || null,
          status:             'not_started',
        });
      } else {
        // ── Update existing SOW task ──
        if (!sowId || !modalForm.task_id || !modalForm.person_id || !modalForm.date) return;
        // Send the share of time and the predecessor only when the person
        // changed them here. Sending them every time overwrote the task's real
        // FTE and replaced every link it had (several predecessors, SS or FF
        // links, lags) with the single finish-to-start link this dialog shows.
        const original = modal?.task || {};
        const patch = {
          owner_id:   modalForm.person_id,
          start_date: modalForm.date,
        };
        const newDep = modalForm.depends_on_task_id || null;
        if (newDep !== (original.depends_on_task_id || null)) patch.depends_on_task_id = newDep;
        const dur = parseFloat(modalForm.duration_days);
        if (!isNaN(dur) && dur > 0) patch.duration_days = dur;
        // Days of work, sent only when changed here (or the person changed).
        const days = parseFloat(modalForm.work_days);
        const originalDays = daysOfWorkFor(original, original.owner_id);
        if (!isNaN(days) && days > 0 && (days !== originalDays || modalForm.person_id !== original.owner_id)) {
          patch.lead_planned_hours = r2(days * DAY_HOURS);
          patch.fte_pct = shareFor(days, { ...original, duration_days: patch.duration_days ?? original.duration_days }, modalForm.person_id);
        }
        await api.put(`sows/${sowId}/project-tasks/${modalForm.task_id}`, patch);
      }
      await refreshPlannerTasks();
      setModal(null);
    } catch (err) {
      setSlotError('Failed to save task. Please try again.');
    } finally {
      setModalSaving(false);
    }
  }

  // Unassign: clear owner + date — task returns to unallocated tray
  async function onModalUnassign() {
    if (!modalForm.sow_id || !modalForm.task_id) return;
    setModalSaving(true);
    try {
      await api.put(`sows/${modalForm.sow_id}/project-tasks/${modalForm.task_id}`, {
        owner_id:   null,
        start_date: null,
      });
      api.get('planner-tasks').then(t => setPlannerTasks(t || []));
      setModal(null);
    } finally {
      setModalSaving(false);
    }
  }

  // ── Slot card (month view) ─────────────────────────────────────────────────
  // NOT rendered as <SlotCard/> so React doesn't remount DOM nodes during drag.
  function renderSlotCard(slot, isStart, isEnd, _date) {
    const isMoving   = movingSlot?.slot.id === slot.id;
    const isResizing = resizingSlot?.slot.id === slot.id;
    const color      = slot.color || '#3B82F6';
    const isMultiDay = slot.end_date && slot.end_date !== slot.date;

    // ── Continuation band (not the start day) ──
    // Now fully clickable to open edit modal.
    if (!isStart) {
      return (
        <div
          key={`cont-${slot.id}-${_date}`}
          className="flex items-center justify-between group cursor-pointer select-none"
          style={{
            height: 26,
            background:   color + '28',
            borderTop:    `2px solid ${color}55`,
            borderBottom: `2px solid ${color}55`,
            borderRight:  isEnd ? `2px solid ${color}55` : 'none',
            borderLeft:   'none',
            borderRadius: isEnd ? '0 4px 4px 0' : 0,
            opacity: isMoving || isResizing ? 0.35 : 1,
          }}
          onClick={e => { e.stopPropagation(); openEditModal(slot); }}
          title={`${slot.label} — click to edit`}
        >
          {/* faint label on continuation */}
          <div className="pl-1 truncate" style={{ color, fontSize: 7, opacity: 0.7, flex: 1 }}>
            {slot.label}
          </div>
          {/* delete on hover */}
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity mr-0.5 flex items-center justify-center rounded"
            style={{ width: 14, height: 14, background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 9, flexShrink: 0 }}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); deleteSlot(slot.id); }}
            title="Remove"
          >×</button>
          {isEnd && (
            <div
              className="flex items-center justify-center cursor-col-resize select-none"
              style={{ color, width: 14, fontSize: 9, flexShrink: 0 }}
              draggable
              onDragStart={e => { e.stopPropagation(); onResizeDragStart(e, slot); }}
              onDragEnd={onDragEnd}
              title="Drag to extend"
            >⟩</div>
          )}
        </div>
      );
    }

    // ── Start (or single) day card ──
    return (
      <div
        key={`slot-${slot.id}`}
        className="relative w-full text-white text-[8px] leading-tight group select-none flex items-stretch"
        style={{
          background:   color,
          borderRadius: isEnd ? 4 : '4px 0 0 4px',
          borderRight:  isEnd ? undefined : 'none',
          height: 26,
          overflow: 'hidden',
          opacity: isMoving || isResizing ? 0.35 : 1,
        }}
        onClick={e => {
          if (e.target.closest('[data-drag-handle]') || e.target.closest('button')) return;
          e.stopPropagation();
          openEditModal(slot);
        }}
        title={[slot.sow_reference && `${slot.sow_reference} ·`, slot.label, slot.notes && `📝 ${slot.notes}`].filter(Boolean).join(' ')}
      >
        {/* Drag handle */}
        <div
          data-drag-handle="1"
          className="flex items-center justify-center cursor-grab shrink-0 opacity-50 hover:opacity-90 transition-opacity"
          style={{ width: 10, fontSize: 7 }}
          draggable
          onDragStart={e => { e.stopPropagation(); onSlotDragStart(e, slot); }}
          onDragEnd={onDragEnd}
          onClick={e => e.stopPropagation()}
          title="Drag to move"
        >⠿</div>

        {/* Content */}
        <div className="flex-1 min-w-0 py-0.5 pr-4 cursor-pointer overflow-hidden">
          {slot.sow_reference && (
            <div className="opacity-70 truncate" style={{ fontSize: 6, lineHeight: 1.2 }}>{slot.sow_reference}</div>
          )}
          <div className="font-semibold truncate" style={{ fontSize: slot.sow_reference ? 7 : 8 }}>{slot.label}</div>
          {slot.notes && (
            <div className="opacity-60 truncate" style={{ fontSize: 6 }}>📝 {slot.notes}</div>
          )}
        </div>

        {/* Delete button */}
        <button
          className="absolute top-0 right-0 w-4 h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/25 select-none"
          style={{ fontSize: 10, lineHeight: 1, borderRadius: '0 4px 4px 0', zIndex: 10 }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); deleteSlot(slot.id); }}
          title="Remove"
        >×</button>

        {/* Resize handle (last day of multi-day slot) */}
        {isEnd && isMultiDay && (
          <div
            className="absolute top-0 right-0 h-full flex items-center justify-center cursor-col-resize select-none"
            style={{ width: 14, fontSize: 9, zIndex: 10 }}
            draggable
            onDragStart={e => onResizeDragStart(e, slot)}
            onDragEnd={onDragEnd}
            title="Drag to extend"
          >⟩</div>
        )}
      </div>
    );
  }

  // ── Month cell ──────────────────────────────────────────────────────────────
  function renderMonthCell(person, day) {
    const isPast    = day < todayStr;
    const isToday   = day === todayStr;
    const isDrop    = dropTarget?.personId === person.id && dropTarget?.date === day;
    const onLeave   = leaveMap[person.id]?.has(day) ?? false;
    const leaveType = leaveDetails[`${person.id}|${day}`] || 'On Leave';

    // Leave day — locked cell
    if (onLeave) {
      return (
        <td
          key={day}
          className={['border-l border-gray-100 align-middle bg-amber-50/70', isToday ? 'ring-1 ring-inset ring-navy/20' : ''].join(' ')}
          style={{ padding: '3px 2px' }}
        >
          <div className="flex items-center justify-center" style={{ minHeight: 56 }}>
            <div className="rounded border border-amber-400 bg-amber-100 px-1 py-1 text-center w-full">
              <div className="text-[8px] font-bold text-amber-600 leading-tight">✈</div>
              <div className="text-[6px] font-semibold text-amber-500 leading-tight uppercase tracking-wide truncate">{leaveType}</div>
            </div>
          </div>
        </td>
      );
    }

    const cellSlots   = slotDayMap[`${person.id}|${day}`] || [];
    const cellOwned   = taskOwnerDayMap[`${person.id}|${day}`] || [];
    const amItems     = cellSlots.filter(x => x.slot.slot_type === 'am');
    const pmItems     = cellSlots.filter(x => x.slot.slot_type === 'pm');
    const fullItems   = cellSlots.filter(x => x.slot.slot_type === 'full_day');
    const hasAnyFull  = fullItems.length > 0;
    // SOW task indicators — slots already covering this day take visual precedence
    const slotTaskIds = new Set(cellSlots.map(x => x.slot.task_id).filter(Boolean));
    const ownedItems  = cellOwned.filter(t => !slotTaskIds.has(t.id));

    return (
      <td
        key={day}
        onDragOver={!isPast ? e => onCellDragOver(e, person.id, day) : undefined}
        onDrop={!isPast ? e => onCellDrop(e, person.id, day) : undefined}
        onClick={() => !isPast && !hasAnyFull && openCreateModal(person.id, day)}
        className={[
          'border-l border-gray-100 align-top transition-colors',
          isToday ? 'bg-navy/5' : '',
          isPast  ? 'bg-gray-50/50' : '',
          !isPast && !hasAnyFull ? 'cursor-pointer hover:bg-blue-50/20' : '',
          isDrop  ? 'bg-blue-50 ring-2 ring-inset ring-blue-300' : '',
        ].join(' ')}
        style={{ padding: '3px 2px', minWidth: 0 }}
      >
        {/* SOW tasks assigned to this person on this day — click to edit, drag to reassign */}
        {ownedItems.length > 0 && (
          <div className="space-y-0.5 mb-0.5">
            {ownedItems.map(t => {
              const color = sowHexMap[t.sow_id] || '#3B82F6';
              const isDraggingThis = draggingOwnedTask?.task.id === t.id;
              return (
                <div
                  key={`owned-${t.id}`}
                  draggable
                  onDragStart={e => { e.stopPropagation(); onOwnedTaskDragStart(e, t); }}
                  onDragEnd={onDragEnd}
                  onClick={e => { e.stopPropagation(); openTaskModal(t); }}
                  className="w-full rounded text-[8px] font-semibold leading-tight px-1.5 py-1 cursor-grab active:cursor-grabbing hover:opacity-80 transition-opacity select-none"
                  style={{
                    background: color + '20',
                    border: `1.5px solid ${color}`,
                    color,
                    opacity: isDraggingThis ? 0.4 : 1,
                  }}
                  title={[t.sow_reference && `${t.sow_reference} ·`, t.name, `Owner: ${t.owner_name}`, 'Drag to reassign · click to edit'].filter(Boolean).join(' ')}
                >
                  {t.sow_reference && <div className="text-[7px] opacity-60 leading-tight">{t.sow_reference}</div>}
                  <div className="truncate">{t.name}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full-day slots */}
        {hasAnyFull && (
          <div className="space-y-0.5">
            {fullItems.map(({ slot, isStart, isEnd }) =>
              renderSlotCard(slot, isStart, isEnd, day)
            )}
            {/* Allow adding more full-day items */}
            {!isPast && (
              <button
                className="w-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-sm"
                style={{ height: 14 }}
                onClick={e => { e.stopPropagation(); openCreateModal(person.id, day, 'full_day'); }}
                title="Add another activity"
              >
                <span className="text-[7px] text-blue-300 font-bold">+ add</span>
              </button>
            )}
          </div>
        )}

        {/* AM / PM split — shown when no full-day slots */}
        {!hasAnyFull && (
          <div className="flex flex-col" style={{ minHeight: 58 }}>

            {/* AM zone */}
            <div className="flex items-stretch gap-0.5" style={{ minHeight: 27 }}>
              <div className="flex items-center justify-center shrink-0 select-none" style={{ width: 9 }}>
                <span className="text-[5px] font-black uppercase text-gray-300 tracking-tight"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>AM</span>
              </div>
              <div
                className={['flex-1 relative rounded-sm', isDrop ? 'bg-blue-50/40' : ''].join(' ')}
                onDragOver={!isPast ? e => { e.stopPropagation(); onCellDragOver(e, person.id, day); } : undefined}
                onDrop={!isPast ? e => { e.stopPropagation(); onCellDrop(e, person.id, day); } : undefined}
              >
                {amItems.length === 0 && !isPast && (
                  <button
                    className="absolute inset-0 w-full flex items-center justify-center opacity-0 hover:opacity-100 hover:bg-blue-50/60 transition-opacity rounded-sm"
                    onClick={e => { e.stopPropagation(); openCreateModal(person.id, day, 'am'); }}
                  >
                    <span className="text-[7px] text-blue-300 font-bold select-none">+</span>
                  </button>
                )}
                <div className="space-y-0.5">
                  {amItems.map(({ slot, isStart, isEnd }) => renderSlotCard(slot, isStart, isEnd, day))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-dashed border-gray-200 mx-0.5" />

            {/* PM zone */}
            <div className="flex items-stretch gap-0.5" style={{ minHeight: 27 }}>
              <div className="flex items-center justify-center shrink-0 select-none" style={{ width: 9 }}>
                <span className="text-[5px] font-black uppercase text-gray-300 tracking-tight"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>PM</span>
              </div>
              <div
                className={['flex-1 relative rounded-sm', isDrop ? 'bg-blue-50/40' : ''].join(' ')}
                onDragOver={!isPast ? e => { e.stopPropagation(); onCellDragOver(e, person.id, day); } : undefined}
                onDrop={!isPast ? e => { e.stopPropagation(); onCellDrop(e, person.id, day); } : undefined}
              >
                {pmItems.length === 0 && !isPast && (
                  <button
                    className="absolute inset-0 w-full flex items-center justify-center opacity-0 hover:opacity-100 hover:bg-blue-50/60 transition-opacity rounded-sm"
                    onClick={e => { e.stopPropagation(); openCreateModal(person.id, day, 'pm'); }}
                  >
                    <span className="text-[7px] text-blue-300 font-bold select-none">+</span>
                  </button>
                )}
                <div className="space-y-0.5">
                  {pmItems.map(({ slot, isStart, isEnd }) => renderSlotCard(slot, isStart, isEnd, day))}
                </div>
              </div>
            </div>

          </div>
        )}
      </td>
    );
  }

  // ── Slot create / edit modal ─────────────────────────────────────────────────
  function renderSlotModal() {
    if (!modal) return null;
    const isEdit      = modal.mode === 'edit';
    const isNewTask   = !isEdit && modalForm.is_new_task;
    const assignedPerson = teamPeople.find(p => p.id === modalForm.person_id);
    const selectedTask   = modalSowTasks.find(t => t.id === modalForm.task_id) || modal.task;
    // Duration display: use form value if edited, else fall back to selected task
    const displayDuration = modalForm.duration_days !== '' ? modalForm.duration_days
      : selectedTask?.duration_days != null ? String(selectedTask.duration_days) : '';
    // Calculated end date preview (workdays)
    const endPreview = (modalForm.date && displayDuration && parseFloat(displayDuration) > 1)
      ? addWorkdays(modalForm.date, parseFloat(displayDuration) - 1)
      : null;
    const canSave = isNewTask
      ? (modalForm.sow_id && modalForm.new_task_name.trim())
      : (modalForm.sow_id && modalForm.task_id && modalForm.person_id && modalForm.date);

    return (
      <Modal
        title={isEdit ? 'Edit Task Assignment' : isNewTask ? 'New SOW Task' : 'Assign Task'}
        onClose={() => setModal(null)}
        footer={
          <div className="flex items-center justify-between gap-2 w-full">
            <div>
              {isEdit && (
                <button
                  onClick={onModalUnassign}
                  disabled={modalSaving}
                  className="text-[10px] px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-40 transition-colors"
                  title="Clear owner and start date — task returns to unallocated tray"
                >↩ Unassign</button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setModal(null)}
                className="text-[10px] px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >Cancel</button>
              <button
                onClick={onModalSave}
                disabled={!canSave || modalSaving}
                className="text-[10px] px-4 py-1.5 rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-40 font-semibold transition-colors"
              >{modalSaving ? 'Saving…' : isEdit ? 'Save Changes' : isNewTask ? 'Create & Assign' : 'Assign Task'}</button>
            </div>
          </div>
        }
      >
          {assignedPerson && !isNewTask && (
            <div className="text-[10px] text-gray-400">
              {assignedPerson.first_name} {assignedPerson.last_name}
              {modalForm.date && ` · ${fmtDay(modalForm.date)}`}
            </div>
          )}

          {/* Mode toggle (create only) */}
          {!isEdit && (
            <div className="flex rounded-lg overflow-hidden border border-gray-200 mt-3 text-[10px]">
              <button
                onClick={() => setModalForm(f => ({ ...f, is_new_task: false }))}
                className={`flex-1 py-1.5 font-semibold transition-colors ${!isNewTask ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >Assign Existing</button>
              <button
                onClick={() => setModalForm(f => ({ ...f, is_new_task: true, task_id: null }))}
                className={`flex-1 py-1.5 font-semibold transition-colors ${isNewTask ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >+ New Task</button>
            </div>
          )}

          <div className="py-4 space-y-3">

            {/* SOW — always shown */}
            <div>
              <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">SOW *</label>
              <SowSelect
                sows={sows}
                value={modalForm.sow_id || ''}
                onChange={id => {
                  setModalForm(f => ({ ...f, sow_id: id || null, task_id: null }));
                  setModalSowTasks([]);
                }}
                disabled={isEdit}
                placeholder="— select SOW —"
                inputClassName="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            {/* ── ASSIGN EXISTING TASK mode ── */}
            {!isNewTask && (
              <>
                {/* Task picker */}
                <div>
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Task *</label>
                  <select
                    value={modalForm.task_id || ''}
                    onChange={e => {
                      const tid = e.target.value || null;
                      const t = modalSowTasks.find(x => x.id === tid);
                      setModalForm(f => ({
                        ...f,
                        task_id: tid,
                        duration_days: t?.duration_days != null ? String(t.duration_days) : f.duration_days,
                      }));
                    }}
                    disabled={!modalForm.sow_id || isEdit}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20 disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    {isEdit ? (
                      /* Edit mode: always show current task name as the single option */
                      <option value={modalForm.task_id}>
                        {modal.task?.name || selectedTask?.name || '—'}
                        {(modal.task?.duration_days ?? selectedTask?.duration_days) ? ` (${modal.task?.duration_days ?? selectedTask?.duration_days}d)` : ''}
                      </option>
                    ) : (
                      <>
                        <option value="">
                          {!modalForm.sow_id ? '— select a SOW first —'
                            : modalSowTasks.length === 0 ? '— loading… —'
                            : '— select task —'}
                        </option>
                        {modalSowTasks.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name}{t.duration_days ? ` (${t.duration_days}d)` : ''}
                            {t.start_date_locked ? ' 🔒' : ''}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Duration */}
                <div>
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                    Duration (days)
                    {selectedTask?.start_date_locked
                      ? <span className="ml-1.5 text-amber-500 font-normal normal-case">🔒 start date locked — only duration updates</span>
                      : endPreview ? <span className="ml-1.5 text-gray-400 font-normal normal-case">→ ends {fmtDay(endPreview)}</span>
                      : null}
                  </label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={displayDuration}
                    onChange={e => setModalForm(f => ({ ...f, duration_days: e.target.value }))}
                    placeholder={selectedTask?.duration_days ?? '1'}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                  />
                </div>

                {/* Days of work — existing task */}
                <div>
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                    Days of work <span className="font-normal text-gray-400 normal-case ml-1">— for this person on this task</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0.5" step="0.5"
                      value={modalForm.work_days}
                      onChange={e => setModalForm(f => ({ ...f, work_days: e.target.value }))}
                      placeholder="days"
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                    {parseFloat(modalForm.work_days) > 0 && selectedTask && (() => {
                      const pct = shareFor(parseFloat(modalForm.work_days), { ...selectedTask, duration_days: parseFloat(modalForm.duration_days) || selectedTask.duration_days }, modalForm.person_id);
                      return <span className="text-[9px] font-bold text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">≈ {pct}% of their time</span>;
                    })()}
                  </div>
                </div>

                {/* Depends On — existing tasks in same SOW, excluding self */}
                <div>
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                    Depends On
                    <span className="ml-1.5 text-gray-300 font-normal normal-case">— cascade will auto-shift this task's date after predecessor</span>
                  </label>
                  <select
                    value={modalForm.depends_on_task_id || ''}
                    onChange={e => setModalForm(f => ({ ...f, depends_on_task_id: e.target.value || null }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                  >
                    <option value="">— no dependency —</option>
                    {modalSowTasks
                      .filter(t => t.id !== modalForm.task_id)
                      .map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.duration_days ? ` (${t.duration_days}d)` : ''}{t.start_date_locked ? ' 🔒' : ''}
                        </option>
                      ))}
                  </select>
                  {modalForm.depends_on_task_id && (() => {
                    const pred = modalSowTasks.find(t => t.id === modalForm.depends_on_task_id);
                    if (!pred?.end_date && !pred?.start_date) return null;
                    return (
                      <div className="mt-1 text-[9px] text-blue-500 px-1">
                        ↳ starts after {pred.name} finishes ({fmtDay(pred.end_date || pred.start_date)}). Set on save.
                      </div>
                    );
                  })()}
                  {(modal?.task?.predecessor_count || 0) > 1 && (
                    <div className="mt-1 text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      This task has {modal.task.predecessor_count} links in its plan, and this list shows one. Leave it as it is to keep them all; choosing a different task here replaces every link with that one. Edit links on the SOW's plan to keep more than one.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── NEW TASK mode ── */}
            {isNewTask && (
              <>
                <div>
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Task Name *</label>
                  <input
                    type="text"
                    value={modalForm.new_task_name}
                    onChange={e => setModalForm(f => ({ ...f, new_task_name: e.target.value }))}
                    placeholder="e.g. Infrastructure Build, SIT Testing…"
                    autoFocus
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Duration (days) *</label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={modalForm.new_task_duration}
                      onChange={e => setModalForm(f => ({ ...f, new_task_duration: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Days of work</label>
                    <input
                      type="number"
                      min="0.5" step="0.5"
                      value={modalForm.new_task_work_days}
                      onChange={e => setModalForm(f => ({ ...f, new_task_work_days: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Team</label>
                    <select
                      value={modalForm.new_task_team_id || ''}
                      onChange={e => setModalForm(f => ({ ...f, new_task_team_id: e.target.value || null }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                    >
                      <option value="">— select team —</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Depends On — for new tasks */}
                {modalForm.sow_id && modalSowTasks.length > 0 && (
                  <div>
                    <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                      Depends On <span className="text-gray-300 font-normal normal-case">(optional)</span>
                    </label>
                    <select
                      value={modalForm.new_task_depends_on || ''}
                      onChange={e => setModalForm(f => ({ ...f, new_task_depends_on: e.target.value || null }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
                    >
                      <option value="">— no dependency —</option>
                      {modalSowTasks.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.duration_days ? ` (${t.duration_days}d)` : ''}{t.start_date_locked ? ' 🔒' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* End date preview for new task */}
                {modalForm.date && parseFloat(modalForm.new_task_duration) > 1 && (
                  <div className="text-[9px] text-gray-400 px-1">
                    → ends {fmtDay(addWorkdays(modalForm.date, parseFloat(modalForm.new_task_duration) - 1))}
                  </div>
                )}
              </>
            )}

            {/* Assigned To — always shown */}
            <div>
              <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                Assigned To {isNewTask ? <span className="text-gray-300 font-normal normal-case">(optional)</span> : '*'}
              </label>
              <select
                value={modalForm.person_id || ''}
                onChange={e => setModalForm(f => ({ ...f, person_id: e.target.value || null }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="">— unassigned (add to team tray) —</option>
                {teamPeople.map(p => (
                  <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.designation ? ` · ${p.designation}` : ''}</option>
                ))}
              </select>
            </div>

            {/* Start Date — always shown */}
            <div>
              <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wide block mb-1">
                Start Date {isNewTask ? <span className="text-gray-300 font-normal normal-case">(optional)</span> : '*'}
              </label>
              <input
                type="date"
                value={modalForm.date}
                onChange={e => setModalForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-navy/20"
              />
            </div>

          </div>
      </Modal>
    );
  }

  // ── Shared header ──────────────────────────────────────────────────────────
  const SharedHeader = (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div>
        <h2 className="text-base font-extrabold text-navy">
          {view === 'week' ? 'Weekly Planner' : 'Month Planner'}
        </h2>
        <div className="text-[9px] text-gray-500">
          {view === 'week'
            ? 'Who is doing what, each day — includes approved leave'
            : 'Drag activities or tasks onto a day · click any card to edit · drag ⟩ to extend'}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Team selector */}
        <div className="flex items-center gap-2">
          {selectedTeam && (
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: selectedTeam.color || '#6B7280' }} />
          )}
          <select
            value={selectedTeamId || ''}
            onChange={e => setSelectedTeamId(e.target.value)}
            className="text-[10px] border border-gray-200 rounded px-2 py-1 bg-white text-navy font-semibold focus:outline-none focus:ring-1 focus:ring-navy/30"
          >
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {/* Week / Month toggle */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-[10px]">
          <button
            onClick={() => setView('week')}
            className={`px-3 py-1 font-semibold transition-colors ${view === 'week' ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
          >Week</button>
          <button
            onClick={() => setView('month')}
            className={`px-3 py-1 font-semibold transition-colors ${view === 'month' ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
          >Month</button>
        </div>
      </div>
    </div>
  );

  // ── Guard renders ──────────────────────────────────────────────────────────
  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading planner…</div>;
  if (loadError) return (
    <div className="text-center py-12">
      <div className="text-sm text-red-500 font-semibold mb-2">Could not load the week planner.</div>
      <div className="text-xs text-gray-400 mb-4">The request timed out or failed. Please try again.</div>
      <button onClick={() => window.location.reload()}
        className="text-xs px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600">
        Reload page
      </button>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  // WEEK VIEW
  // ══════════════════════════════════════════════════════════════════════════════
  if (view === 'week') {
    const getTasksForDay = (personId, dayStr) => taskDayMap[`${personId}|${dayStr}`] || [];
    const isOnLeave      = (personId, dayStr) => leaveMap[personId]?.has(dayStr) ?? false;
    const getLeaveType   = (personId, dayStr) => leaveDetails[`${personId}|${dayStr}`] || 'On Leave';

    return (
      <div className="fade-in">
        {SharedHeader}

        {/* Week navigator */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="text-[9px] px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 font-semibold text-gray-600 transition-colors">← Prev</button>
          <span className="text-[11px] font-bold text-navy px-1">{fmtWeek(weekStart)}</span>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="text-[9px] px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 font-semibold text-gray-600 transition-colors">Next →</button>
          <button onClick={() => setWeekStart(weekMonday(todayStr))}
            className="text-[9px] px-2.5 py-1 rounded border border-navy/30 bg-navy/5 text-navy hover:bg-navy/10 font-semibold transition-colors ml-1">Today</button>
          <div className="ml-auto flex items-center gap-3 text-[9px] text-gray-400">
            {leaveLoading && <span className="text-gray-300 italic">Loading leave…</span>}
            {weekStats.leaveDays > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                {weekStats.leaveDays} day{weekStats.leaveDays !== 1 ? 's' : ''} leave
              </span>
            )}
            {weekStats.taskDays > 0 && (
              <span>{weekStats.taskDays} task day{weekStats.taskDays !== 1 ? 's' : ''} assigned</span>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-3 mb-2 text-[8px]">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block" />
            <span className="text-gray-500">Assigned to task (colour = SOW)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-400 inline-block" />
            <span className="text-gray-500">On approved leave</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-white border border-gray-200 inline-block" />
            <span className="text-gray-500">Free / unassigned</span>
          </div>
        </div>

        {/* Grid */}
        {teamPeople.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 text-[10px] text-amber-700">
            No active staff assigned to <strong>{selectedTeam?.name || 'this team'}</strong>.
            Assign people via the People section.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded overflow-x-auto shadow-sm">
            <table className="w-full" style={{ minWidth: '720px' }}>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold sticky left-0 bg-gray-50 z-10 min-w-[140px]">Person</th>
                  {weekDays.map(day => {
                    const isToday = day === todayStr;
                    return (
                      <th key={day} className={`px-2 py-2 text-center text-[8px] font-bold border-l border-gray-200 min-w-[150px] ${isToday ? 'bg-navy/5 text-navy' : 'text-gray-500'}`}>
                        <div>{fmtDay(day)}</div>
                        {isToday && <div className="text-[7px] text-navy/60 font-semibold mt-0.5">◆ Today</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {teamPeople.map((person, pi) => (
                  <tr key={person.id} className={`border-b border-gray-100 last:border-0 ${pi % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white align-top">
                      <div className="flex items-center gap-1.5">
                        <Avatar personId={person.id} className="w-6 h-6 rounded-full text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0"
                          style={{ background: person.avatar_color || '#1B2A4A' }}>
                          {(person.first_name?.[0] || '') + (person.last_name?.[0] || '')}
                        </Avatar>
                        <div>
                          <div className="text-[10px] font-semibold text-navy leading-tight">{person.first_name} {person.last_name}</div>
                          {person.designation && <div className="text-[7px] text-gray-400 leading-tight">{person.designation}</div>}
                        </div>
                      </div>
                      {/* Their whole week: every SOW, against their own capacity */}
                      {loadByPerson.get(person.id)?.week && (() => {
                        const w = loadByPerson.get(person.id).week;
                        return (
                          <div className="mt-1.5 pr-1" title={`This week across all SOWs: ${w.committed_days}d committed${w.tentative_days ? `, ${w.tentative_days}d bid` : ''} against ${w.capacity_days}d available`}>
                            <LoadBar committed={w.committed_days} tentative={w.tentative_days} capacity={w.capacity_days} />
                          </div>
                        );
                      })()}
                    </td>
                    {weekDays.map(day => {
                      const onLeave  = isOnLeave(person.id, day);
                      const dayTasks = onLeave ? [] : getTasksForDay(person.id, day);
                      const isToday  = day === todayStr;
                      const isDropTarget = dropTarget?.personId === person.id && dropTarget?.date === day;
                      return (
                        <td key={day}
                          onDragOver={e => onCellDragOver(e, person.id, day)}
                          onDrop={e => onCellDrop(e, person.id, day)}
                          className={`px-1.5 py-1.5 border-l border-gray-100 align-top transition-colors
                            ${onLeave                 ? 'bg-amber-50/60' : ''}
                            ${isToday && !onLeave     ? 'bg-navy/5' : ''}
                            ${isToday && onLeave      ? 'bg-amber-50' : ''}
                            ${isDropTarget            ? 'ring-2 ring-inset ring-blue-400 bg-blue-50/60' : ''}`}
                          style={{ minHeight: '60px' }}>
                          {(() => {
                            // Why this day is short, and how full it is, from the engine.
                            const d = loadByPerson.get(person.id)?.days.get(day);
                            if (!d) return null;
                            const label = { bank_holiday: 'Bank holiday', closure: 'Closed', non_working: 'Not a working day', not_employed: 'Not employed', half_leave: 'Half day off' }[d.reason];
                            const over = d.committed_days - d.capacity_days > 0.01;
                            return (
                              <div className="flex items-center justify-between gap-1 mb-0.5 text-[7px] leading-none">
                                <span className={d.reason && d.reason !== 'leave' ? 'text-sky-700 font-semibold' : 'text-gray-300'}>{label || ''}</span>
                                {(d.committed_days > 0 || d.tentative_days > 0) && (
                                  <span className={over ? 'text-red-600 font-bold' : 'text-gray-400'}
                                        title={`${d.committed_days}d booked of ${d.capacity_days}d available${d.tentative_days ? `, plus ${d.tentative_days}d bid` : ''}`}>
                                    {Math.round(d.committed_days * 10) / 10}/{Math.round(d.capacity_days * 10) / 10}d{over ? ' over' : ''}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {onLeave ? (
                            <div className="rounded border border-amber-400 bg-amber-100 px-1.5 py-1">
                              <div className="text-[7px] font-bold text-amber-600 leading-tight uppercase tracking-wide">✈ {getLeaveType(person.id, day)}</div>
                            </div>
                          ) : dayTasks.length === 0 ? (
                            <div className="flex items-center justify-center h-8">
                              <span className="text-[9px] text-gray-200">—</span>
                            </div>
                          ) : (
                            <div className="space-y-0.5">
                              {dayTasks.map(t => {
                                const c = sowColorMap[t.sow_id] || { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700' };
                                const isDraggingThis = draggingOwnedTask?.task.id === t.id;
                                return (
                                  <div key={t.id}
                                    draggable
                                    onDragStart={e => { e.stopPropagation(); onOwnedTaskDragStart(e, t); }}
                                    onDragEnd={onDragEnd}
                                    onClick={() => openTaskModal(t)}
                                    className={`rounded border px-1.5 py-1 cursor-grab active:cursor-grabbing hover:opacity-80 transition-opacity ${c.bg} ${c.border}`}
                                    style={{ opacity: isDraggingThis ? 0.4 : 1 }}
                                    title={`${t.sow_reference ? t.sow_reference + ' · ' : ''}${t.sow_detail || ''}\n${t.name}\nDrag to move · click to edit`}>
                                    {t.sow_reference && <div className={`text-[7px] font-bold leading-tight opacity-60 ${c.text}`}>{t.sow_reference}</div>}
                                    {t.sow_detail && <div className={`text-[8px] font-bold leading-tight ${c.text}`}>{t.sow_detail}</div>}
                                    <div className={`text-[8px] leading-tight ${c.text} ${t.sow_detail ? 'opacity-75' : 'font-semibold'}`}>{t.name}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* API error banner */}
        {slotError && (
          <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[10px] text-red-700">
            <span className="font-bold shrink-0">⚠</span>
            <span className="flex-1">{slotError}</span>
            <button onClick={() => setSlotError(null)} className="text-red-400 hover:text-red-600 font-bold shrink-0">✕</button>
          </div>
        )}

        {/* ── Unallocated team tasks tray ──────────────────────────────────── */}
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">
              Unallocated Team Tasks
            </div>
            {tasksLoading ? (
              <span className="text-[8px] text-gray-300 italic">Loading tasks…</span>
            ) : (
              <span className="text-[8px] text-gray-400">
                — {unallocatedTasks.length} task{unallocatedTasks.length !== 1 ? 's' : ''} for {selectedTeam?.name || 'this team'} not yet scheduled · drag onto any day to assign
              </span>
            )}
          </div>

          <div
            className={[
              'flex flex-wrap gap-2 p-3 rounded-lg border-2 border-dashed min-h-[52px] transition-colors',
              unallocatedTasks.length === 0 ? 'border-gray-200 bg-gray-50/40' : 'border-amber-300 bg-amber-50/30',
            ].join(' ')}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { setDraggingTask(null); setDropTarget(null); }}
          >
            {unallocatedTasks.length === 0 && !tasksLoading && (
              <div className="text-[9px] text-gray-300 italic self-center">
                All tasks for this team have been scheduled, or no tasks exist yet.
                Add tasks via the SOW Plan to see them here.
              </div>
            )}
            {unallocatedTasks.map(task => {
              const color          = sowHexMap[task.sow_id] || '#6B7280';
              const isDraggingThis = draggingTask?.task.id === task.id;
              const durLabel       = task.duration_days && task.duration_days > 1 ? `${task.duration_days}d` : null;
              return (
                <div
                  key={task.id}
                  draggable
                  onDragStart={e => onTaskDragStart(e, task)}
                  onDragEnd={onDragEnd}
                  className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-white text-[9px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-sm transition-opacity"
                  style={{
                    background: color,
                    opacity:    isDraggingThis ? 0.4 : 1,
                    maxWidth:   220,
                  }}
                  title={[
                    task.sow_reference && `${task.sow_reference} · ${task.sow_detail || ''}`,
                    task.name,
                    `${task.duration_days || 1} working day${(task.duration_days || 1) !== 1 ? 's' : ''}`,
                    task.owner_name  && `Owner: ${task.owner_name}`,
                    task.start_date  && `Starts: ${task.start_date}`,
                  ].filter(Boolean).join('\n')}
                >
                  {task.sow_reference && (
                    <div className="flex flex-col leading-tight shrink-0 opacity-80">
                      <span style={{ fontSize: 7 }}>{task.sow_reference}</span>
                      {task.sow_detail && <span className="font-normal opacity-80 truncate max-w-[120px]" style={{ fontSize: 6.5 }}>{task.sow_detail}</span>}
                    </div>
                  )}
                  <span className="truncate">{task.name}</span>
                  {durLabel && (
                    <span className="rounded bg-black/20 px-1 py-0.5 font-bold shrink-0" style={{ fontSize: 7 }}>{durLabel}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-2 text-[8px] text-gray-400">
          Tasks come from SOW plans. Drag a task card to reassign it to a different person or day.
          Drag from the tray below to schedule unassigned tasks. Click any card to edit details.
          Leave overlays approved requests from the holiday system. Colours group tasks by SOW.
        </div>

        {/* Modal */}
        {renderSlotModal()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // MONTH VIEW — full rework
  // ══════════════════════════════════════════════════════════════════════════════
  const currentMonthStart = firstOfMonth(todayStr);
  const canGoPrev = addMonths(monthStart, -1) >= currentMonthStart;

  return (
    <div className="fade-in">
      {SharedHeader}

      {/* API error banner */}
      {slotError && (
        <div className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[10px] text-red-700">
          <span className="font-bold shrink-0">⚠</span>
          <span className="flex-1">{slotError}</span>
          <button onClick={() => setSlotError(null)} className="text-red-400 hover:text-red-600 font-bold shrink-0">✕</button>
        </div>
      )}

      {/* Month navigator */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => canGoPrev && setMonthStart(addMonths(monthStart, -1))}
          disabled={!canGoPrev}
          className="text-[9px] px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 font-semibold text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >← Prev</button>
        <span className="text-[11px] font-bold text-navy px-1">{fmtMonthYear(monthStart)}</span>
        <button
          onClick={() => setMonthStart(addMonths(monthStart, 1))}
          className="text-[9px] px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 font-semibold text-gray-600 transition-colors"
        >Next →</button>
        <button
          onClick={() => setMonthStart(currentMonthStart)}
          className="text-[9px] px-2.5 py-1 rounded border border-navy/30 bg-navy/5 text-navy hover:bg-navy/10 font-semibold transition-colors ml-1"
        >This Month</button>
        {(slotsLoading || tasksLoading) && <span className="text-[9px] text-gray-300 italic ml-2">Loading…</span>}
        <div className="ml-auto text-[9px] text-gray-400 hidden sm:block">
          Click a cell to add · click any card to edit · drag card to move · drag ⟩ to extend
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-3 mb-2 text-[8px] flex-wrap">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          <span className="text-gray-400">Coloured card = planned activity (click to edit)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-gray-200 border border-dashed border-gray-300 inline-block" />
          <span className="text-gray-400">Grey = past (locked)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-semibold text-gray-500">AM / PM</span>
          <span className="text-gray-400">= hover top/bottom half to add half-day slots</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded border border-amber-400 bg-amber-100 inline-block" />
          <span className="text-gray-400">✈ Approved leave (locked)</span>
        </div>
      </div>

      {/* Grid — one compact table per week */}
      {teamPeople.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-[10px] text-amber-700">
          No active staff assigned to <strong>{selectedTeam?.name || 'this team'}</strong>.
          Assign people via the People section.
        </div>
      ) : (
        <div className="space-y-3">
          {monthWeeks.map((week, wi) => {
            const containsToday = week.includes(todayStr);
            return (
              <div
                key={week[0]}
                className={[
                  'bg-white border rounded-lg shadow-sm overflow-hidden',
                  containsToday ? 'border-navy/30' : 'border-gray-200',
                ].join(' ')}
                onDragLeave={e => {
                  if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(null);
                }}
              >
                {/* Week banner */}
                <div className={[
                  'flex items-center justify-between px-3 py-1.5 border-b',
                  containsToday ? 'bg-navy/5 border-navy/20' : 'bg-gray-50 border-gray-100',
                ].join(' ')}>
                  <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">
                    {fmtWeekRange(week)}
                  </span>
                  {containsToday && (
                    <span className="text-[8px] font-semibold text-navy bg-navy/10 px-2 py-0.5 rounded-full">
                      Current week
                    </span>
                  )}
                </div>

                <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 148 }} />
                    {week.map(d => <col key={d} />)}
                  </colgroup>
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-400 font-bold sticky left-0 bg-white z-10" />
                      {week.map(day => {
                        const isToday = day === todayStr;
                        const isPast  = day < todayStr;
                        return (
                          <th
                            key={day}
                            className={[
                              'py-1.5 text-center text-[8px] font-bold border-l border-gray-100',
                              isToday ? 'bg-navy/5 text-navy' : '',
                              isPast  ? 'text-gray-300' : 'text-gray-500',
                            ].join(' ')}
                          >
                            {fmtDayHeader(day)}
                            {isToday && <div className="text-[6px] text-navy/40 font-semibold">◆ today</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {teamPeople.map((person, pi) => (
                      <tr key={person.id} className={`border-b border-gray-100 last:border-0 ${pi % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                        {/* Person column */}
                        <td className="px-2 py-1.5 sticky left-0 z-10 border-r border-gray-100 align-middle"
                          style={{ background: pi % 2 === 1 ? 'rgb(249 250 251 / 0.3)' : 'white' }}>
                          <div className="flex items-center gap-1.5">
                            <Avatar personId={person.id} className="w-6 h-6 rounded-full text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0"
                              style={{ background: person.avatar_color || '#1B2A4A' }}>
                              {(person.first_name?.[0] || '') + (person.last_name?.[0] || '')}
                            </Avatar>
                            <div className="min-w-0">
                              <div className="text-[10px] font-semibold text-navy leading-tight truncate">{person.first_name} {person.last_name}</div>
                              {person.designation && <div className="text-[7px] text-gray-400 leading-tight truncate">{person.designation}</div>}
                            </div>
                          </div>
                        </td>
                        {week.map(day => renderMonthCell(person, day))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Unallocated team tasks tray ──────────────────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">
            Unallocated Team Tasks
          </div>
          {tasksLoading ? (
            <span className="text-[8px] text-gray-300 italic">Loading tasks…</span>
          ) : (
            <span className="text-[8px] text-gray-400">
              — {unallocatedTasks.length} task{unallocatedTasks.length !== 1 ? 's' : ''} for {selectedTeam?.name || 'this team'} not yet scheduled · drag onto any day to assign
            </span>
          )}
        </div>

        <div
          className={[
            'flex flex-wrap gap-2 p-3 rounded-lg border-2 border-dashed min-h-[52px] transition-colors',
            unallocatedTasks.length === 0 ? 'border-gray-200 bg-gray-50/40' : 'border-amber-300 bg-amber-50/30',
          ].join(' ')}
          onDragOver={e => e.preventDefault()}
          onDrop={() => { setDraggingTask(null); setDropTarget(null); }}
        >
          {unallocatedTasks.length === 0 && !tasksLoading && (
            <div className="text-[9px] text-gray-300 italic self-center">
              All tasks for this team have been scheduled, or no tasks exist yet.
              Add tasks via the SOW Plan to see them here.
            </div>
          )}
          {unallocatedTasks.map(task => {
            const color        = sowHexMap[task.sow_id] || '#6B7280';
            const isDraggingThis = draggingTask?.task.id === task.id;
            const durLabel     = task.duration_days && task.duration_days > 1 ? `${task.duration_days}d` : null;
            return (
              <div
                key={task.id}
                draggable
                onDragStart={e => onTaskDragStart(e, task)}
                onDragEnd={onDragEnd}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-white text-[9px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-sm transition-opacity"
                style={{
                  background: color,
                  opacity:    isDraggingThis ? 0.4 : 1,
                  maxWidth:   220,
                }}
                title={[
                  task.sow_reference && `${task.sow_reference} · ${task.sow_detail || ''}`,
                  task.name,
                  `${task.duration_days || 1} working day${(task.duration_days || 1) !== 1 ? 's' : ''}`,
                  task.owner_name   && `Owner: ${task.owner_name}`,
                  task.start_date   && `Starts: ${task.start_date}`,
                ].filter(Boolean).join('\n')}
              >
                {task.sow_reference && (
                  <span className="opacity-70 font-normal shrink-0" style={{ fontSize: 7 }}>{task.sow_reference}</span>
                )}
                <span className="truncate">{task.name}</span>
                {durLabel && (
                  <span className="rounded bg-black/20 px-1 py-0.5 font-bold shrink-0" style={{ fontSize: 7 }}>{durLabel}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 text-[8px] text-gray-400">
        Month planner slots are independent of SOW task assignments and are stored separately.
        Switch to Week view to see SOW-derived assignments with leave overlay.
        Changes to the planner do not affect SOW task data.
      </div>

      {/* Modal */}
      {renderSlotModal()}
    </div>
  );
}
